import { useMutation } from '@apollo/client'
import {
    AskMessageMutation,
    GiveFeedbackMutation,
} from 'api/graphQL/mutations/askDelaChat.mutation'
import {
    AskMessageInput,
    AskMessageResult,
    GiveFeedbackInput,
    GiveFeedbackResult,
} from 'api/graphQL/types/askDelaChat.types'
import { html_beautify } from 'js-beautify'
import React from 'react'
import { copyHTMLElement, copyToClipboard } from 'shared/utils/clipboardUtils'
import { v4 as newGuid } from 'uuid'
import { ChatDisclaimerMessage } from './ChatDisclaimer'
import { useChatProfileStore } from './ChatProfileStore'
import { AbortMessage, ErrorMessage, Message } from './message'
import { useChatMessageStore } from './useChatMessageStore'

/**
 * Represents a chat bot.
 */
export interface ChatBot {
    /**
     * An array of chat messages.
     */
    messages: Message[]

    /**
     * Indicates whether the chat messages are currently being loaded.
     */
    isLoading: boolean

    /**
     * Checks if the specified message is the last message in the chat messages.
     * @param message - The message to check.
     */
    isLastMessage: (message: Message) => boolean

    /**
     * The abort controller used to cancel asking a message.
     */
    askMessageAborter?: AbortController

    /**
     * Sets the chat messages to the specified array of messages.
     * @param messages - The array of messages to set.
     */
    setMessages: (messages: Message[]) => void

    /**
     * Create a user message from a user input.
     * @param userInput The user input to create a message from.
     * @returns The created user message.
     */
    createUserMessage: (userInput: string) => Message

    /**
     * Sends a user input to the chat bot.
     * @param prompt - The prompt message with user input.
     */
    askMessage: (prompt: Message) => void

    /**
     * Sends a suggested message to the chat bot.
     * @param suggestedMessage - The suggested message object.
     */
    askSuggestedMessage: (suggestedMessage: Message) => void

    /**
     * Cancels asking the current message
     */
    cancelAskMessage: () => void

    /**
     * Gives feedback on a message.
     * @param message - The message to give feedback on.
     * @param isLiked - Indicates whether the message is liked or not.
     */
    giveFeedback: (message: Message, isLiked: boolean) => void

    /**
     * Regenerates a message from the chat bot.
     * @param agentMessage - The agent message to regenerate.
     */
    regenerateMessage: (agentMessage: Message) => void

    /**
     * Copies a message.
     * @param message - The message to copy.
     * @returns A promise that resolves to a boolean indicating whether the message was successfully copied.
     */
    copyMessage: (message: Message) => Promise<boolean>

    /**
     * Copies the HTML and Plain Text content of an HTMLElement to the clipboard
     * @param htmlElement HTML Element to be copied
     * @returns A promise that resolves to a boolean indicating whether the message was successfully copied.
     */
    copyMessageHTML: (htmlElement: HTMLElement) => Promise<boolean>

    /**
     * Indicates whether the disclaimer is open.
     */
    isDisclaimerOpen: boolean

    /**
     * Sets whether the disclaimer is open.
     * @param state a boolean value indicating whether the disclaimer is open.
     */
    setIsDisclaimerOpen: (state: boolean) => void
}

export interface ChatBotOptions {
    /**
     * The timeout in milliseconds for asking a message.
     */
    askTimeoutMs: number
}

const defaultOptions: ChatBotOptions = {
    askTimeoutMs: 120000,
}

export const useChatBot = ({
    askTimeoutMs,
}: ChatBotOptions = defaultOptions): ChatBot => {
    const chatUser = useChatProfileStore((state) => state.chatUser)
    const messages = useChatMessageStore((state) => state.messages)
    const isLoading = useChatMessageStore((state) => state.isLoading)
    const isDisclaimerOpen = useChatMessageStore(
        (state) => state.isDisclaimerOpen
    )
    const setMessages = useChatMessageStore((state) => state.setMessages)
    const addMessage = useChatMessageStore((state) => state.addMessage)
    const removeMessages = useChatMessageStore((state) => state.removeMessages)
    const startLoading = useChatMessageStore((state) => state.startLoading)
    const stopLoading = useChatMessageStore((state) => state.stopLoading)
    const setMessageFeedback = useChatMessageStore(
        (state) => state.setMessageFeedback
    )
    const askMessageAborter = useChatMessageStore(
        (state) => state.askMessageAborter
    )
    const createAskMessageAborter = useChatMessageStore(
        (state) => state.createAskMessageAborter
    )
    const limitResponses = useChatMessageStore((state) => state.limitResponses)
    const setIsDisclaimerOpen = useChatMessageStore(
        (state) => state.setIsDisclaimerOpen
    )

    const [askMessageMutation] = useMutation<AskMessageResult, AskMessageInput>(
        AskMessageMutation,
        {
            onCompleted: React.useCallback(
                (data: AskMessageResult) => {
                    if (!data) {
                        return
                    }

                    const { id, answer, promptMessageId } = data.ask

                    // convert the response to a message model
                    const responseMessage: Message = {
                        id,
                        userId: chatUser?.id,
                        role: 'agent',
                        content: answer,
                        dateCreated: new Date(),
                        promptMessageId,
                    }

                    // add the actual response message to the conversation
                    addMessage(responseMessage)

                    // updates the list of messages to limit by set number
                    limitResponses()

                    stopLoading()
                },
                [chatUser]
            ),
            onError: React.useCallback((error) => {
                // Add an error message
                addMessage(ErrorMessage)

                stopLoading()
            }, []),
        }
    )

    const triggerAsk = React.useCallback((prompt: Message) => {
        startLoading()

        // Create an abort controller to handle cancellation
        const aborter = createAskMessageAborter()
        const signal = aborter.signal

        signal.onabort = () => {
            // Handle the abortion by adding a message and stopping the loading state
            addMessage(AbortMessage)
            stopLoading()
        }

        // Set the timeout
        const timeout = setTimeout(() => {
            aborter.abort()
        }, askTimeoutMs)

        askMessageMutation({
            variables: {
                input: {
                    id: prompt.id,
                    question: prompt.content,
                },
            },
            context: {
                fetchOptions: {
                    signal,
                },
            },
        }).finally(() => {
            // Ensure cleanup
            clearTimeout(timeout)
        })
    }, [])

    const [giveFeedbackMutation] = useMutation<
        GiveFeedbackResult,
        GiveFeedbackInput
    >(GiveFeedbackMutation)

    const triggerGiveFeedback = React.useCallback(
        (question: Message, answer: Message, isLiked: boolean) => {
            const input: GiveFeedbackInput = {
                input: {
                    id: answer.id, // We use the id of the answer, as this is associated with the feedback
                    userId: chatUser?.id ?? '',
                    userEmail: chatUser?.email ?? '',
                    question: question.content,
                    dateAsked: question.dateCreated,
                    answer: answer.content,
                    dateAnswered: answer.dateCreated,
                    isLiked: isLiked,
                    feedbackDate: new Date(),
                },
            }

            giveFeedbackMutation({
                variables: input,
            })
        },
        [chatUser]
    )

    const createUserMessage = React.useCallback(
        (userInput: string) => {
            const prompt: Message = {
                id: newGuid(),
                userId: chatUser?.id,
                role: 'user',
                content: userInput,
                dateCreated: new Date(),
            }

            return prompt
        },
        [chatUser]
    )

    const askMessage = React.useCallback(
        (prompt: Message) => {
            // No question asked by the user
            if (!prompt.content) {
                return
            }

            addMessage(prompt)

            // Call the API to get a response
            triggerAsk(prompt)
        },
        [triggerAsk]
    )

    const askSuggestedMessage = React.useCallback(
        (suggestedMessage: Message) => {
            // Only suggested messages can be asked
            if (suggestedMessage.role !== 'suggested') {
                return
            }

            const message = createUserMessage(suggestedMessage.content)

            askMessage(message)
        },
        [askMessage, createUserMessage]
    )

    const cancelAskMessage = React.useCallback(() => {
        askMessageAborter?.abort()
    }, [askMessageAborter])

    const giveFeedback = React.useCallback(
        (agentMessage: Message, isLiked: boolean) => {
            // We can only give feedback to agent messages
            if (agentMessage.role !== 'agent') {
                return
            }

            // We can only give feedback once
            if (agentMessage.isLiked !== undefined) {
                return
            }

            // Set the local state
            setMessageFeedback(agentMessage, isLiked)

            // find the prompt for the agent message we want to give feedback on
            const prompt = messages.find(
                (m) => m.id === agentMessage.promptMessageId
            )
            if (!prompt) {
                return
            }

            // Call the API to save feedback
            triggerGiveFeedback(prompt, agentMessage, isLiked)
        },
        []
    )

    const regenerateMessage = React.useCallback(
        (agentMessage: Message) => {
            // find the prompt for the agent message we want to regenerate
            const prompt = messages.find(
                (m) => m.id === agentMessage.promptMessageId
            )

            if (!prompt || agentMessage.role !== 'agent') {
                return
            }

            // remove the old response from the conversation
            removeMessages((m) => m.id === agentMessage.id)

            // Call the API again with the same user message
            triggerAsk(prompt)
        },
        [messages, triggerAsk]
    )

    const copyMessage = React.useCallback((message: Message) => {
        // We are legally required to append the disclaimer when copying the message
        const content = `${message.content}\n\n${ChatDisclaimerMessage}`
        const contentToCopy = html_beautify(content)
        return copyToClipboard(contentToCopy)
    }, [])

    const copyMessageHTML = React.useCallback((htmlElement: HTMLElement) => {
        // We are legally required to append the disclaimer when copying the message
        const htmlContent = `${htmlElement.innerHTML}\n\n${ChatDisclaimerMessage}`
        const plainTextContent = `${htmlElement.innerText}\n\n${ChatDisclaimerMessage}`

        return copyHTMLElement(htmlContent, plainTextContent)
    }, [])

    const isLastMessage = React.useCallback(
        (message: Message) => {
            // handle empty list
            if (messages.length <= 0) {
                return false
            }

            // check if the message is the last message in the list
            const lastMessage = messages[messages.length - 1]
            return message.id === lastMessage.id
        },
        [messages]
    )

    return {
        messages,
        isLoading,
        isDisclaimerOpen,
        askMessageAborter,
        isLastMessage,
        setMessages,
        createUserMessage,
        askMessage,
        askSuggestedMessage,
        cancelAskMessage,
        giveFeedback,
        regenerateMessage,
        copyMessage,
        copyMessageHTML,
        setIsDisclaimerOpen,
    }
}
